import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useDisclosure,
  Badge,
  Flex,
  Input,
  Select,
  IconButton,
  Tooltip,
  useToast,
  Spinner,
  Text,
  Card,
  CardBody,
} from "@chakra-ui/react";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  FiEdit,
  FiEye,
  FiToggleRight,
  FiToggleLeft,
  FiUserPlus,
} from "react-icons/fi";
import UserDetailsModal from "./UserDetailsModal";
import UserEditModal from "./UserEditModal";
import { useNavigate } from "react-router-dom";
import UserService from "../../Pages/User/UserService";
import OptionsService from "../../Pages/UserRegistration/OptionsService";
import ModernPageShell from "../../components/layout/ModernPageShell";
import { getModernUi } from "../../components/layout/modernUi";
import { formatAccessRoleLabel } from "./accessRoleUtils";

export default function Usuarios({ modern = false }) {
  const ui = getModernUi(modern);
  const navigate = useNavigate();
  const toast = useToast();
  const [allUsers, setAllUsers] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const [selectedUser, setSelectedUser] = useState(null);

  const [searchName, setSearchName] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const usersData = await UserService.getAllUsers();
      setAllUsers(usersData);

      setUsuarios(
        usersData.map((user) => ({
          ...user,
          nome: user.name || "Sem nome",
          cargo: user.position ? user.position.description : "N/A",
          status: user.status ? user.status.description : "N/A",
          role: formatAccessRoleLabel(user.role?.description),
          empresa: user.company ? user.company.description : "N/A",
        }))
      );

      const fetchedStatusOptions = await OptionsService.getOptionsByType(
        "status"
      );
      const fetchedPositionOptions = await OptionsService.getOptionsByType(
        "employees"
      );
      const fetchedRoleOptions = await OptionsService.getOptionsByType("role");
      const fetchedCompanyOptions = await OptionsService.getOptionsByType(
        "Company"
      );

      setStatusOptions(fetchedStatusOptions);
      setPositionOptions(fetchedPositionOptions);
      setRoleOptions(fetchedRoleOptions);
      setCompanyOptions(fetchedCompanyOptions);
    } catch (err) {
      setError(
        "Não foi possível carregar os dados. Verifique sua conexão ou permissões."
      );
      console.error("Erro ao carregar dados em Usuarios.jsx:", err);
      toast({
        title: "Erro ao carregar dados.",
        description: "Não foi possível buscar os dados de usuários e opções.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredUsuarios = useMemo(() => {
    return usuarios.filter((user) => {
      const nome = String(user.nome || user.name || "").toLowerCase();
      const status = String(user.status || "").toLowerCase();
      const matchName = nome.includes(searchName.toLowerCase());
      const matchStatus =
        filterStatus === "todos" || status === filterStatus;
      return matchName && matchStatus;
    });
  }, [searchName, filterStatus, usuarios]);

  const handleDetails = useCallback(
    (mappedUser) => {
      const originalUser = allUsers.find(
        (u) => u.employmentsId === mappedUser.employmentsId
      );
      if (originalUser) {
        setSelectedUser(originalUser);
        onDetailsOpen();
      } else {
        toast({
          title: "Erro",
          description: "Usuário não encontrado para detalhes.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [onDetailsOpen, allUsers, toast]
  );

  const handleEdit = useCallback(
    (mappedUser) => {
      const originalUser = allUsers.find(
        (u) => u.employmentsId === mappedUser.employmentsId
      );
      if (originalUser) {
        setSelectedUser(originalUser);
        onEditOpen();
      } else {
        toast({
          title: "Erro",
          description: "Usuário não encontrado para edição.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [onEditOpen, allUsers, toast]
  );

  const toggleStatus = useCallback(
    async (userToToggle) => {
      setSelectedUser(userToToggle);
      setIsLoading(true);

      try {
        const originalUser = allUsers.find(
          (u) => u.employmentsId === userToToggle.employmentsId
        );
        if (!originalUser) {
          throw new Error(
            "Usuário original não encontrado para alternar status."
          );
        }

        const newStatusDescription =
          String(userToToggle.status || "").toLowerCase() === "ativo"
            ? "Inativo"
            : "Ativo";

        const newStatusObject = statusOptions.find(
          (opt) =>
            opt.description.toLowerCase() === newStatusDescription.toLowerCase()
        );

        if (!newStatusObject) {
          throw new Error(
            `Objeto de status para '${newStatusDescription}' não encontrado nas opções carregadas.`
          );
        }

        const updatedPayload = {
          ...originalUser,
          status: newStatusObject,
        };

        if (updatedPayload.authorities) {
          delete updatedPayload.authorities;
        }

        await UserService.updateUser(updatedPayload);

        setUsuarios((prevUsuarios) =>
          prevUsuarios.map((user) =>
            user.employmentsId === userToToggle.employmentsId
              ? { ...user, status: newStatusDescription }
              : user
          )
        );

        toast({
          title: "Status atualizado.",
          description: `${userToToggle.nome} foi ${
            newStatusDescription.toLowerCase() === "ativo"
              ? "ativado"
              : "inativado"
          } com sucesso.`,
          status:
            newStatusDescription.toLowerCase() === "ativo"
              ? "success"
              : "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } catch (error) {
        console.error("Erro ao atualizar status do usuário:", error);
        toast({
          title: "Erro ao atualizar status.",
          description:
            error.response?.data?.message ||
            "Não foi possível atualizar o status do usuário.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setIsLoading(false);
        setSelectedUser(null);
      }
    },
    [toast, allUsers, statusOptions]
  );

  const pageContent = (
    <>
      {!modern && (
        <Heading
          mb={8}
          size={{ base: "lg", md: "xl" }}
          textAlign="center"
          color="red.700"
          fontWeight="extrabold"
          letterSpacing="wide"
          pb={2}
          borderBottom="2px solid"
          borderColor="red.100"
        >
          Gerenciamento de Usuários
        </Heading>
      )}

      <Card mb={8} {...ui.panel} p={{ base: 4, md: 6 }}>
        <CardBody p={0}>
          <Flex
            gap={{ base: 4, md: 6 }}
            flexWrap="wrap"
            justify={{ base: "center", md: "space-between" }}
            align="center"
          >
            <Input
              placeholder="Buscar por nome..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              flex="1"
              minW="200px"
              maxW={{ base: "full", md: "350px" }}
              bg="white"
              borderColor="gray.200"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "red.500",
                boxShadow: "0 0 0 1px red.500",
              }}
              borderRadius="lg"
              size="lg"
            />
            <Select
              flex="0.5"
              minW="150px"
              maxW={{ base: "full", md: "200px" }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              bg="white"
              borderColor="gray.200"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "red.500",
                boxShadow: "0 0 0 1px red.500",
              }}
              borderRadius="lg"
              size="lg"
            >
              <option value="todos">Todos os Status</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </Select>
            <Button
              colorScheme="red"
              leftIcon={<FiUserPlus />}
              size="lg"
              borderRadius="lg"
              boxShadow="md"
              _hover={{
                bg: "red.600",
                boxShadow: "lg",
                transform: "translateY(-1px)",
              }}
              transition="all 0.2s ease-in-out"
              onClick={() => navigate("/cadastro-usuario")}
            >
              Adicionar Usuário
            </Button>
          </Flex>
        </CardBody>
      </Card>

      <Card {...ui.panel} overflow="hidden">
        <CardBody p={0}>
          <Box overflowX="auto" border="none" borderRadius="lg">
            <Table variant="simple" colorScheme="gray" size="md" minW="700px">
              <Thead
                bg="gray.100"
                borderBottom="2px solid"
                borderColor="gray.200"
              >
                <Tr>
                  <Th
                    py={4}
                    px={6}
                    fontSize="sm"
                    textTransform="uppercase"
                    color="gray.700"
                    fontFamily="heading"
                  >
                    Nome
                  </Th>
                  <Th
                    py={4}
                    px={6}
                    fontSize="sm"
                    textTransform="uppercase"
                    color="gray.700"
                    fontFamily="heading"
                  >
                    Cargo
                  </Th>
                  <Th
                    py={4}
                    px={6}
                    fontSize="sm"
                    textTransform="uppercase"
                    color="gray.700"
                    fontFamily="heading"
                  >
                    Empresa
                  </Th>
                  <Th
                    py={4}
                    px={6}
                    fontSize="sm"
                    textTransform="uppercase"
                    color="gray.700"
                    fontFamily="heading"
                  >
                    Nível de Acesso (perfil)
                  </Th>
                  <Th
                    py={4}
                    px={6}
                    fontSize="sm"
                    textTransform="uppercase"
                    color="gray.700"
                    fontFamily="heading"
                  >
                    Status
                  </Th>
                  <Th
                    py={4}
                    px={6}
                    textAlign="center"
                    minW="220px"
                    fontSize="sm"
                    textTransform="uppercase"
                    color="gray.700"
                    fontFamily="heading"
                  >
                    Ações
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={10}>
                      <Spinner size="xl" color="red.500" thickness="4px" />
                      <Text mt={4} fontSize="lg" color="gray.600">
                        Carregando usuários...
                      </Text>
                    </Td>
                  </Tr>
                ) : error ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={6}>
                      <Text color="red.500" fontSize="lg" fontWeight="semibold">
                        {error}
                      </Text>
                    </Td>
                  </Tr>
                ) : filteredUsuarios.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={6}>
                      <Text color="gray.500" fontSize="lg" fontStyle="italic">
                        Nenhum usuário encontrado com os filtros aplicados.
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredUsuarios.map((user) => (
                    <Tr
                      key={user.employmentsId}
                      _hover={{
                        bg: "red.50",
                        transition: "background-color 0.2s ease-in-out",
                      }}
                    >
                      <Td fontWeight="medium" color="gray.800">
                        {user.nome}
                      </Td>
                      <Td color="gray.700">{user.cargo}</Td>
                      <Td color="gray.700">{user.empresa}</Td>
                      <Td color="gray.700">{user.role}</Td>
                      <Td>
                        <Badge
                          colorScheme={
                            String(user.status || "").toLowerCase() === "ativo"
                              ? "green"
                              : "red"
                          }
                          fontSize="0.85em"
                          px={3}
                          py={1}
                          borderRadius="full"
                          textTransform="uppercase"
                          fontWeight="bold"
                          boxShadow="sm"
                        >
                          {user.status}
                        </Badge>
                      </Td>
                      <Td>
                        <Flex justify="center" gap={3} flexWrap="wrap">
                          <Tooltip label="Ver Detalhes" hasArrow>
                            <IconButton
                              aria-label="Detalhes"
                              icon={<FiEye />}
                              size="md"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleDetails(user)}
                              _hover={{ bg: "blue.50", color: "blue.600" }}
                              color="blue.500"
                            />
                          </Tooltip>
                          <Tooltip
                            label={
                              String(user.status || "").toLowerCase() === "ativo"
                                ? "Inativar Usuário"
                                : "Ativar Usuário"
                            }
                            hasArrow
                          >
                            <IconButton
                              aria-label={
                                String(user.status || "").toLowerCase() === "ativo"
                                  ? "Inativar"
                                  : "Ativar"
                              }
                              icon={
                                String(user.status || "").toLowerCase() === "ativo" ? (
                                  <FiToggleLeft />
                                ) : (
                                  <FiToggleRight />
                                )
                              }
                              size="md"
                              colorScheme={
                                String(user.status || "").toLowerCase() === "ativo"
                                  ? "orange"
                                  : "green"
                              }
                              variant="solid"
                              onClick={() => toggleStatus(user)}
                              isLoading={
                                isLoading &&
                                selectedUser?.employmentsId ===
                                  user.employmentsId
                              }
                            />
                          </Tooltip>
                          <Tooltip label="Editar Usuário" hasArrow>
                            <IconButton
                              aria-label="Editar"
                              icon={<FiEdit />}
                              size="md"
                              colorScheme="red"
                              variant="solid"
                              onClick={() => handleEdit(user)}
                              _hover={{ bg: "red.600", boxShadow: "md" }}
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      {isDetailsOpen && (
        <UserDetailsModal
          user={selectedUser}
          isOpen={isDetailsOpen}
          onClose={onDetailsClose}
          onEdit={() => {
            onDetailsClose();
            onEditOpen();
          }}
        />
      )}

      {isEditOpen && (
        <UserEditModal
          user={selectedUser}
          isOpen={isEditOpen}
          onClose={onEditClose}
          statusOptions={statusOptions}
          positionOptions={positionOptions}
          roleOptions={roleOptions}
          companyOptions={companyOptions}
          onUpdateSuccess={fetchData}
        />
      )}
    </>
  );

  if (modern) {
    return (
      <ModernPageShell
        title="Usuários"
        subtitle="Consulte, edite e gerencie os colaboradores do sistema."
        eyebrow="Administração"
      >
        {pageContent}
      </ModernPageShell>
    );
  }

  return (
    <Box
      maxW={{ base: "95%", md: "1200px" }}
      mx="auto"
      p={{ base: 4, md: 10 }}
      bg="gray.50"
      boxShadow="lg"
      borderRadius="xl"
      my={10}
    >
      {pageContent}
    </Box>
  );
}
